import { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  Plus,
  Truck,
  Building2,
  Barrel,
  ReceiptEuro,
  FileBadge,
  MessageSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface SidebarUserProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

export function SidebarUser({ currentPage, onNavigate }: SidebarUserProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isQuickAccessExpanded, setIsQuickAccessExpanded] = useState(true);

  const mainNavItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'gestion-operationnelle-user', label: 'Mes Chantiers', icon: Building2 },
    { id: 'mes-transports-user', label: 'Mes Transports', icon: Truck },
    { id: 'mes-cap-user', label: 'Mes Déchets', icon: Barrel },
    { id: 'devis-factures', label: 'Devis & Factures', icon: ReceiptEuro },
    { id: 'mes-demandes', label: 'Mes Demandes', icon: MessageSquare },
  ];

  const quickAccessLinks = [
    { id: 'nouvelle-demande-chantier', label: 'Nouveau Chantier', icon: Plus },
    { id: 'nouvelle-demande-transport', label: 'Demande de Transport', icon: Truck },
    { id: 'documents-administratifs', label: 'Documents Administratifs', icon: FileBadge },
    { id: 'nouveau-site', label: 'Mes Entreprises Travaux', icon: Building2 },
    { id: 'gestion-etablissement', label: 'Mon Établissement', icon: Building2 },
  ];

  // Classes navigation item. On force avec ! pour battre le variant ghost
  // shadcn qui applique hover:text-accent-foreground (navy sombre = invisible
  // sur le fond navy de la sidebar).
  const navItemBase =
    'w-full justify-start h-auto py-2 px-3 border border-transparent rounded-lg font-medium';
  const navItemNormal =
    '!text-sidebar-foreground/[0.78] hover:!bg-sidebar-accent/[0.07] hover:!text-sidebar-foreground';
  const navItemActive =
    '!bg-sidebar-accent/[0.07] !text-sidebar-foreground !border-brand hover:!bg-sidebar-accent/[0.07] hover:!text-sidebar-foreground';

  return (
    <TooltipProvider>
      <nav className={`bg-sidebar border-r border-sidebar-border h-[calc(100vh-56px)] relative transition-all duration-300 flex-shrink-0 ${isCollapsed ? 'w-16' : 'w-[261px]'}`}>
        {/* Bouton Toggle */}
        <div className="absolute -right-3 top-12 z-10">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 rounded-full !bg-sidebar !border-sidebar-border border p-0 !text-sidebar-foreground hover:!border-brand hover:!text-sidebar-foreground"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                >
                  {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isCollapsed ? 'Étendre' : 'Réduire'}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <div className={`px-4 ${isCollapsed ? 'pt-4' : 'pt-12'} pb-4 overflow-hidden`}>
          {/* Eyebrow section : Interface utilisateur (dot bleu = demande-client) */}
          {!isCollapsed && (
            <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase font-medium text-sidebar-foreground/[0.45] px-3 mb-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-demande-client" aria-hidden="true" />
              Interface utilisateur
            </div>
          )}

          {/* Navigation principale */}
          <div className="space-y-1 mb-6">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;
              const iconClass = isActive
                ? 'text-sidebar-primary'
                : 'text-sidebar-foreground/[0.55]';

              if (isCollapsed) {
                return (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>
                      <div>
                        <Button
                          variant="ghost"
                          className={`w-full justify-start px-2 border border-transparent rounded-lg ${isActive ? navItemActive : navItemNormal}`}
                          onClick={() => onNavigate(item.id)}
                        >
                          <Icon className={`h-5 w-5 flex-shrink-0 ${iconClass}`} />
                        </Button>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.label}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  className={`${navItemBase} ${isActive ? navItemActive : navItemNormal}`}
                  onClick={() => onNavigate(item.id)}
                >
                  <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${iconClass}`} />
                  <span className="text-left">{item.label}</span>
                </Button>
              );
            })}
          </div>

          {/* Section Actions Rapides - mode collapsed (icones seules + tooltip) */}
          {isCollapsed && (
            <div className="border-t border-sidebar-border pt-4 space-y-1">
              {quickAccessLinks.map((link) => {
                const Icon = link.icon;
                const isActive = currentPage === link.id;
                const iconClass = isActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/[0.55]';
                return (
                  <Tooltip key={link.id}>
                    <TooltipTrigger asChild>
                      <div>
                        <Button
                          variant="ghost"
                          className={`w-full justify-start px-2 border border-transparent rounded-lg ${isActive ? navItemActive : navItemNormal}`}
                          onClick={() => onNavigate(link.id)}
                        >
                          <Icon className={`h-5 w-5 flex-shrink-0 ${iconClass}`} />
                        </Button>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{link.label}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}

          {/* Section Actions Rapides - mode expanded */}
          {!isCollapsed && (
            <div className="border-t border-sidebar-border pt-4">
              <button
                onClick={() => setIsQuickAccessExpanded(!isQuickAccessExpanded)}
                className="w-full flex items-center justify-between px-3 py-1 text-[10px] tracking-[0.16em] uppercase font-medium text-sidebar-foreground/[0.45] hover:text-sidebar-foreground transition-colors"
              >
                <span>Accès rapide</span>
                {isQuickAccessExpanded
                  ? <ChevronDown className="h-3.5 w-3.5 text-sidebar-foreground/[0.55]" />
                  : <ChevronRight className="h-3.5 w-3.5 text-sidebar-foreground/[0.55]" />
                }
              </button>

              {isQuickAccessExpanded && (
                <div className="mt-2 space-y-1">
                  {quickAccessLinks.map((link) => {
                    const Icon = link.icon;
                    const isActive = currentPage === link.id;
                    const iconClass = isActive
                      ? 'text-sidebar-primary'
                      : 'text-sidebar-foreground/[0.55]';
                    return (
                      <Button
                        key={link.id}
                        variant="ghost"
                        size="sm"
                        className={`${navItemBase} text-sm ${isActive ? navItemActive : navItemNormal}`}
                        onClick={() => onNavigate(link.id)}
                      >
                        <Icon className={`h-4 w-4 mr-3 flex-shrink-0 ${iconClass}`} />
                        <span className="truncate">{link.label}</span>
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </TooltipProvider>
  );
}
